import {
  ALL_FORMATS,
  AudioBufferSink,
  AudioSample,
  AudioSampleSource,
  BlobSource,
  BufferTarget,
  CanvasSink,
  CanvasSource,
  canEncodeAudio,
  canEncodeVideo,
  Input,
  Mp4OutputFormat,
  Output,
  UrlSource,
} from 'mediabunny';
import { ReviewClip, ReviewFrameProvider, ReviewScene, ReviewTimeline, ReviewVisual } from 'modules/match-video/domain/interfaces';
import {
  BACKGROUND_COLOR,
  OUTPUT_AUDIO_BITRATE,
  OUTPUT_CHANNELS,
  OUTPUT_FRAME_RATE,
  OUTPUT_HEIGHT,
  OUTPUT_SAMPLE_RATE,
  OUTPUT_VIDEO_BITRATE,
  OUTPUT_WIDTH,
} from './constants';
import { createFrameCanvas, FrameCanvas, FrameContext, releaseFrameCanvas } from './frame-canvas';
import { concatAudioBuffers, normalizeAudio } from './normalize-audio';
import { introPictureScene, platePictureScene, toReviewScene } from './picture-scenes';
import { plateWindow } from './plates';
import { ReleaseBag } from './release';
import { drawFadeThroughBlack } from './transitions';

/** `template-render` — the intro or a plate could not be drawn from its media-manager template. */
export type ReviewAssemblyErrorCode = 'unsupported-browser' | 'unreadable-clip' | 'template-render';

export class ReviewAssemblyError extends Error {
  constructor(
    readonly code: ReviewAssemblyErrorCode,
    readonly clipName?: string,
  ) {
    super(clipName ? `${code}: ${clipName}` : code);
    this.name = 'ReviewAssemblyError';
  }
}

export interface AssembleReviewParams {
  timeline: ReviewTimeline;
  /** Intro and plates; without it — a black intro and no plates. */
  frames?: ReviewFrameProvider;
  signal?: AbortSignal;
  /** 0…1, by clips done; the intro and transitions are too short to count. */
  onProgress?: (progress: number) => void;
}

const FRAME_SEC = 1 / OUTPUT_FRAME_RATE;
const CANVAS_POOL_SIZE = 2;

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new DOMException('The review assembly was cancelled', 'AbortError');
  }
}

export async function isReviewAssemblySupported(): Promise<boolean> {
  const [video, audio] = await Promise.all([
    canEncodeVideo('avc', { width: OUTPUT_WIDTH, height: OUTPUT_HEIGHT, bitrate: OUTPUT_VIDEO_BITRATE }),
    canEncodeAudio('aac', {
      numberOfChannels: OUTPUT_CHANNELS,
      sampleRate: OUTPUT_SAMPLE_RATE,
      bitrate: OUTPUT_AUDIO_BITRATE,
    }),
  ]);
  return video && audio;
}

/**
 * A scene from the provider — a picture is wrapped into its still look. A cancellation passes through, any other
 * failure is a template one.
 */
async function loadScene(
  get: (() => Promise<ReviewVisual | null>) | undefined,
  still: (picture: CanvasImageSource) => ReviewScene,
  signal?: AbortSignal,
): Promise<ReviewScene | null> {
  if (!get) return null;
  try {
    const visual = await get();
    const scene = visual && toReviewScene(visual, still);
    if (signal?.aborted) {
      scene?.dispose();
      throwIfAborted(signal);
    }
    return scene;
  } catch (error) {
    if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw error;
    console.error(error);
    throw new ReviewAssemblyError('template-render');
  }
}

function toInputSource(clip: ReviewClip) {
  return clip.source.type === 'file' ? new BlobSource(clip.source.file) : new UrlSource(clip.source.url);
}

function fill(ctx: FrameContext) {
  ctx.globalAlpha = 1;
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
}

/** A scene may leave transforms, clips and alpha behind — the next tick starts clean. */
function drawScene(ctx: FrameContext, scene: ReviewScene, timeSec: number, windowSec: number) {
  ctx.save();
  try {
    scene.draw(ctx, timeSec, windowSec);
  } finally {
    ctx.restore();
  }
}

/**
 * Assembles the review MP4 (H.264 + AAC, 1920×1080) from the timeline, entirely in the browser.
 *
 * Clips are opened one at a time and released right after; the encoded result is the only thing kept until the
 * returned `Blob`. Cancelling through `signal` rejects with an `AbortError` and frees everything the same way.
 */
export async function assembleReview({ timeline, frames, signal, onProgress }: AssembleReviewParams) {
  throwIfAborted(signal);
  if (!(await isReviewAssemblySupported())) {
    throw new ReviewAssemblyError('unsupported-browser');
  }

  const bag = new ReleaseBag();
  const main = createFrameCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
  const previous = createFrameCanvas(OUTPUT_WIDTH, OUTPUT_HEIGHT);
  bag.add(() => releaseFrameCanvas(main.canvas));
  bag.add(() => releaseFrameCanvas(previous.canvas));

  const target = new BufferTarget();
  const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
  const videoSource = new CanvasSource(main.canvas, { codec: 'avc', bitrate: OUTPUT_VIDEO_BITRATE });
  const audioSource = new AudioSampleSource({ codec: 'aac', bitrate: OUTPUT_AUDIO_BITRATE });
  output.addVideoTrack(videoSource, { frameRate: OUTPUT_FRAME_RATE });
  output.addAudioTrack(audioSource);

  const clipCount = timeline.items.filter((item) => item.type === 'clip').length;
  let clipsDone = 0;
  let videoTime = 0;
  let audioFrames = 0;

  const addAudio = async (buffer: AudioBuffer) => {
    const samples = AudioSample.fromAudioBuffer(buffer, audioFrames / OUTPUT_SAMPLE_RATE);
    for (const sample of samples) {
      await audioSource.add(sample);
      sample.close();
    }
    audioFrames += buffer.length;
  };

  /** A piece drawn by code, with silence: the intro and transitions. `progress` 0…1, `timeSec` from its start. */
  const addGeneratedPiece = async (durationSec: number, draw: (progress: number, timeSec: number) => void) => {
    const frames = Math.max(1, Math.round(durationSec * OUTPUT_FRAME_RATE));
    for (let i = 0; i < frames; i++) {
      throwIfAborted(signal);
      draw(frames === 1 ? 1 : i / (frames - 1), i * FRAME_SEC);
      await videoSource.add(videoTime + i * FRAME_SEC, FRAME_SEC);
    }
    videoTime += durationSec;
    await addAudio(await normalizeAudio(null, durationSec));
  };

  /** The intro scene; a scene of its own length (a built-in set) overrides the timeline's. */
  const addIntro = async (timelineSec: number) => {
    const intro = await loadScene(frames && (() => frames.intro()), introPictureScene, signal);
    if (intro) bag.add(() => intro.dispose());
    const durationSec = intro?.durationSec ?? timelineSec;
    await addGeneratedPiece(durationSec, (_, timeSec) => {
      fill(main.ctx);
      if (intro) {
        drawScene(main.ctx, intro, timeSec, durationSec);
      }
    });
    intro?.dispose();
  };

  const snapshotPrevious = (): FrameCanvas => {
    previous.ctx.globalAlpha = 1;
    previous.ctx.clearRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    previous.ctx.drawImage(main.canvas, 0, 0);
    return previous.canvas;
  };

  /** A transition followed by its clip: the fade needs the clip's first frame. */
  const addClip = async (clip: ReviewClip, transitionSec: number | null) => {
    const plate = await loadScene(frames && (() => frames.plate(clip)), platePictureScene, signal);
    if (plate) bag.add(() => plate.dispose());
    const input = new Input({ formats: ALL_FORMATS, source: toInputSource(clip) });
    bag.add(() => input.dispose());

    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack || !(await videoTrack.canDecode())) {
      throw new ReviewAssemblyError('unreadable-clip', clip.name);
    }
    const start = await videoTrack.getFirstTimestamp();
    const sourceDurationSec = Math.max(0, (await videoTrack.computeDuration()) - start);
    // The clip goes out at the output frame rate: the track metadata snaps every timestamp to it, so a 60 fps
    // source added frame by frame would get pairs of frames on one timestamp.
    const tickCount = Math.max(1, Math.round(sourceDurationSec * OUTPUT_FRAME_RATE));
    const durationSec = tickCount * FRAME_SEC;
    // A pool reuses two canvases instead of a new 8 MB one per decoded frame: without it a 60 fps clip piles up
    // canvases faster than GC frees them. Safe here — each frame is drawn onto `main` before the next is read.
    const sink = new CanvasSink(videoTrack, {
      width: OUTPUT_WIDTH,
      height: OUTPUT_HEIGHT,
      fit: 'contain',
      poolSize: CANVAS_POOL_SIZE,
    });

    if (transitionSec !== null) {
      const from = snapshotPrevious();
      const first = await sink.getCanvas(start);
      await addGeneratedPiece(transitionSec, (progress) =>
        drawFadeThroughBlack(main.ctx, from, first?.canvas ?? null, progress, OUTPUT_WIDTH, OUTPUT_HEIGHT),
      );
    }

    // Every output tick shows the source frame on screen at that moment; a frame between ticks is skipped,
    // a frame longer than a tick is repeated. Each tick is drawn anew: the plate moves while the frame holds.
    let tick = 0;
    let current: FrameCanvas | null = null;
    const slot = plate && plateWindow(durationSec);
    const drawTick = () => {
      fill(main.ctx);
      if (current) {
        main.ctx.drawImage(current, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      }
      const plateTime = slot ? tick * FRAME_SEC - slot.startSec : -1;
      if (plate && slot && plateTime >= 0 && plateTime < slot.durationSec) {
        drawScene(main.ctx, plate, plateTime, slot.durationSec);
      }
    };
    const emitTicksUntil = async (endSec: number) => {
      while (tick < tickCount && tick * FRAME_SEC < endSec - 1e-6) {
        throwIfAborted(signal);
        drawTick();
        await videoSource.add(videoTime + tick * FRAME_SEC, FRAME_SEC);
        tick += 1;
      }
    };
    for await (const frame of sink.canvases()) {
      throwIfAborted(signal);
      const frameEnd = frame.timestamp - start + frame.duration;
      if (tick < tickCount && tick * FRAME_SEC < frameEnd - 1e-6) {
        current = frame.canvas;
        await emitTicksUntil(frameEnd);
        onProgress?.(Math.min(1, (clipsDone + tick / tickCount) / clipCount));
      }
    }
    // A track shorter than its declared duration: the last frame holds to the end. Its pooled canvas is still
    // intact — the pool reuses a canvas only when another frame is asked for.
    await emitTicksUntil(Number.POSITIVE_INFINITY);
    plate?.dispose();

    // Decoded audio of one clip is tens of MB at most; it is brought to the output format in one go.
    const audioTrack = await input.getPrimaryAudioTrack();
    let audio: AudioBuffer | null = null;
    if (audioTrack && (await audioTrack.canDecode())) {
      const chunks: AudioBuffer[] = [];
      for await (const chunk of new AudioBufferSink(audioTrack).buffers(start)) {
        throwIfAborted(signal);
        chunks.push(chunk.buffer);
      }
      audio = concatAudioBuffers(chunks);
    }
    await addAudio(await normalizeAudio(audio, durationSec));

    videoTime += durationSec;
    input.dispose();
    clipsDone += 1;
    onProgress?.(clipsDone / clipCount);
  };

  try {
    await output.start();

    let pendingTransition: number | null = null;
    for (const item of timeline.items) {
      throwIfAborted(signal);
      if (item.type === 'intro') {
        await addIntro(item.durationSec);
      } else if (item.type === 'transition') {
        pendingTransition = item.durationSec;
      } else {
        await addClip(item.clip, pendingTransition);
        pendingTransition = null;
      }
    }

    await output.finalize();
    const buffer = target.buffer;
    if (!buffer) {
      throw new Error('The review output is empty');
    }
    return new Blob([buffer], { type: 'video/mp4' });
  } catch (error) {
    if (output.state === 'started' || output.state === 'pending') {
      await output.cancel().catch(() => undefined);
    }
    throw error;
  } finally {
    bag.releaseAll();
  }
}
